import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Minus, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { fmtPrice } from "../components/Reveal";
import { useStore } from "../context/StoreContext";
import { useLang } from "../context/LanguageContext";

export default function Cart() {
  const { cart, updateQty, removeFromCart, cartTotal } = useStore();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (cart.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: cart.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: email || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(t("quote.error"));
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 min-h-screen" data-testid="cart-page">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 pb-32">
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="font-display font-extrabold tracking-tighter text-[clamp(2.4rem,6vw,5rem)] leading-none mb-12">{t("cart.title")}</motion.h1>

        {cart.length === 0 ? (
          <div className="py-20 border-t border-ink/10">
            <p className="text-ink/50 font-mono mb-6">{t("cart.empty")}</p>
            <Link to="/catalogo" className="inline-flex items-center gap-2 bg-blue text-[color:var(--bg)] px-6 py-3 hover:bg-gold hover:text-inverse transition-colors">{t("cart.goCatalog")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-12">
            <div className="border-t border-ink/10">
              {cart.map(({ product, quantity }) => (
                <div key={product.id} className="flex gap-5 py-6 border-b border-ink/10" data-testid={`cart-row-${product.slug}`}>
                  <div className="w-28 h-28 bg-surface overflow-hidden shrink-0"><img src={product.image} alt={product.name} className="w-full h-full object-cover" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-4">
                      <div>
                        <Link to={`/producto/${product.slug}`}><h3 className="font-display font-medium text-lg hover:text-gold transition-colors">{product.name}</h3></Link>
                        <p className="text-sm text-ink/50 mt-1">{fmtPrice(product.price, product.currency)}</p>
                      </div>
                      <button onClick={() => removeFromCart(product.id)} className="text-ink/40 hover:text-red-600 transition-colors h-fit"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center border border-ink/15">
                        <button onClick={() => updateQty(product.id, quantity - 1)} className="p-2 hover:text-gold"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="px-3 text-sm font-mono">{quantity}</span>
                        <button onClick={() => updateQty(product.id, quantity + 1)} className="p-2 hover:text-gold"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <span className="font-display font-medium">{fmtPrice(product.price * quantity, product.currency)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:sticky lg:top-28 lg:self-start bg-surface p-8">
              <h3 className="font-display font-extrabold text-2xl mb-6">{t("cart.summary")}</h3>
              <div className="flex justify-between py-2 text-ink/60"><span>{t("cart.subtotal")}</span><span>{fmtPrice(cartTotal)}</span></div>
              <div className="flex justify-between py-2 text-ink/60 border-b border-ink/10 mb-4"><span>{t("cart.taxes")}</span><span className="font-mono text-sm">{t("cart.taxNote")}</span></div>
              <div className="flex justify-between items-baseline mb-6">
                <span className="font-display font-medium text-lg">{t("cart.total")}</span>
                <span className="font-display font-extrabold text-3xl" data-testid="checkout-total">{fmtPrice(cartTotal)}</span>
              </div>

              <label className="block text-sm text-ink/60 mb-2">{t("cart.emailLabel")}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.com" data-testid="checkout-email"
                className="w-full border border-ink/15 bg-[color:var(--bg)] px-4 py-3 mb-5 outline-none focus:border-gold transition-colors" />

              <button onClick={checkout} disabled={loading} data-testid="checkout-btn"
                className="w-full bg-blue text-[color:var(--bg)] py-4 flex items-center justify-center gap-2 hover:bg-gold hover:text-inverse transition-colors duration-300 font-medium disabled:opacity-60">
                <Lock className="w-4 h-4" /> {loading ? t("cart.redirecting") : t("cart.pay")}
              </button>
              <p className="text-xs text-ink/40 mt-4 font-mono text-center">{t("cart.secure")}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
